import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';

export const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const SIGNUP_PATHS = ['/p/signup', '/signup'];

const isSignupCallToAction = callToAction => {
  const href = callToAction?.href;
  const path = typeof href === 'string' ? href.split(/[?#]/)[0].replace(/\/$/, '') : '';
  return callToAction?.fieldType === 'internalButtonLink' && SIGNUP_PATHS.includes(path);
};

const withoutSignupCallToAction = item => {
  if (!item || typeof item !== 'object') {
    return item;
  }

  const callToAction = isSignupCallToAction(item.callToAction)
    ? { fieldType: 'none' }
    : item.callToAction;
  const blocks = item.blocks?.map(withoutSignupCallToAction);

  return {
    ...item,
    ...(item.callToAction ? { callToAction } : {}),
    ...(item.blocks ? { blocks } : {}),
  };
};

export const pageDataForViewer = (pageData, isAuthenticated) => {
  if (!isAuthenticated || !pageData?.sections) {
    return pageData;
  }

  return {
    ...pageData,
    sections: pageData.sections
      .filter(section => !isSignupCallToAction(section.callToAction))
      .map(withoutSignupCallToAction),
  };
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error, isAuthenticated } = props;
  const pageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  return (
    <PageBuilder
      pageAssetsData={pageDataForViewer(pageData, isAuthenticated)}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  isAuthenticated: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};
  const isAuthenticated = state.auth?.isAuthenticated || false;

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return {
    pageAssetsData,
    featuredListingData,
    getListingEntitiesById,
    inProgress,
    error,
    isAuthenticated,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
